import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dropzone", "fileInput", "preview", "fallback", "fileName", "error", "removeButton", "persistedRemoval", "progress", "progressLabel"]
  static values = { maxSize: Number, maxSizeLabel: String, acceptedTypes: Array, acceptedTypesLabel: String }

  connect() {
    this.abortController = new AbortController()
    this.originalPreviewSrc = this.hasPreviewTarget ? this.previewTarget.getAttribute('src') : null
    this.originalPreviewHidden = this.hasPreviewTarget ? this.previewTarget.classList.contains('hidden') : true
    this.setupDropzone()
  }

  disconnect() {
    this.abortController.abort()
    this.releasePreviewUrl()
  }

  setupDropzone() {
    const { signal } = this.abortController

    if (this.hasRemoveButtonTarget) {
      this.removeButtonTarget.addEventListener('click', (event) => this.removeImage(event), { signal })
    }

    ;['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      this.dropzoneTarget.addEventListener(eventName, this.preventDefaults, { signal })
    })

    ;['dragenter', 'dragover'].forEach(eventName => {
      this.dropzoneTarget.addEventListener(eventName, () => this.highlight(), { signal })
    })

    ;['dragleave', 'drop'].forEach(eventName => {
      this.dropzoneTarget.addEventListener(eventName, () => this.unhighlight(), { signal })
    })

    this.dropzoneTarget.addEventListener('drop', (e) => this.handleDrop(e), { signal })
  }

  preventDefaults(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  highlight() {
    this.dropzoneTarget.classList.add('border-blue-500', 'bg-blue-50')
  }

  unhighlight() {
    this.dropzoneTarget.classList.remove('border-blue-500', 'bg-blue-50')
  }

  handleDrop(e) {
    const dt = e.dataTransfer
    const files = dt.files
    
    if (files.length > 0) {
      this.handleFile(files[0])
    }
  }

  triggerFileSelect() {
    this.fileInputTarget.click()
  }

  handleFileSelect(e) {
    const files = e.target.files
    if (files.length > 0) {
      this.handleFile(files[0])
    }
  }

  handleFile(file) {
    this.clearError()
    
    // Validate file type
    const validTypes = this.hasAcceptedTypesValue ? this.acceptedTypesValue : this.fileInputTarget.accept.split(',')
    if (!validTypes.includes(file.type)) {
      this.showError(`Please upload a ${this.acceptedTypesLabelValue} image`)
      return
    }

    // Validate file size
    if (file.size > this.maxSizeValue) {
      this.showError(`File size must be no more than ${this.maxSizeLabelValue}`)
      return
    }

    // Create a new FileList containing our file
    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(file)
    this.fileInputTarget.files = dataTransfer.files
    if (this.hasPersistedRemovalTarget) this.persistedRemovalTarget.checked = false

    // Show preview
    this.showPreview(file)
  }

  showPreview(file) {
    this.releasePreviewUrl()

    if (this.hasPreviewTarget) {
      this.previewUrl = URL.createObjectURL(file)
      this.previewTarget.src = this.previewUrl
      this.previewTarget.onload = () => this.releasePreviewUrl()
      this.previewTarget.classList.remove('hidden')
    }

    if (this.hasFallbackTarget) this.fallbackTarget.classList.add('hidden')

    if (this.hasFileNameTarget) {
      this.fileNameTarget.textContent = file.name
      this.fileNameTarget.classList.remove('hidden')
    }

    if (this.hasRemoveButtonTarget) this.removeButtonTarget.classList.remove('hidden')

    this.dropzoneTarget.classList.add('has-image')
  }

  removeImage(e) {
    e.preventDefault()
    
    // Clear the file input
    this.fileInputTarget.value = ''
    
    // Hide preview elements
    this.restoreOriginalPreview()
    
    if (this.hasFileNameTarget) {
      this.fileNameTarget.textContent = ''
      this.fileNameTarget.classList.add('hidden')
    }
    
    if (this.hasRemoveButtonTarget) {
      this.removeButtonTarget.classList.add('hidden')
    }
    
    // Reset dropzone appearance
    this.dropzoneTarget.classList.remove('has-image')
    this.resetProgress()
    this.clearError()
  }

  uploadStart() {
    this.updateProgress(0)
  }

  uploadProgress(event) {
    this.updateProgress(Math.round(event.detail.progress))
  }

  uploadError(event) {
    event.preventDefault()
    this.showError(event.detail.error || 'Image upload failed. Please try again.')
    this.resetProgress()
  }

  uploadEnd() {
    this.updateProgress(100, 'Upload complete')
  }

  updateProgress(value, label = `${value}% uploaded`) {
    if (this.hasProgressTarget) {
      this.progressTarget.value = value
      this.progressTarget.classList.remove('hidden')
    }

    if (this.hasProgressLabelTarget) {
      this.progressLabelTarget.textContent = label
      this.progressLabelTarget.classList.remove('hidden')
    }
  }

  resetProgress() {
    if (this.hasProgressTarget) {
      this.progressTarget.value = 0
      this.progressTarget.classList.add('hidden')
    }

    if (this.hasProgressLabelTarget) {
      this.progressLabelTarget.textContent = ''
      this.progressLabelTarget.classList.add('hidden')
    }
  }

  restoreOriginalPreview() {
    this.releasePreviewUrl()
    if (!this.hasPreviewTarget) return

    this.previewTarget.src = this.originalPreviewSrc || ''
    this.previewTarget.classList.toggle('hidden', this.originalPreviewHidden)
    if (this.hasFallbackTarget) this.fallbackTarget.classList.toggle('hidden', !this.originalPreviewHidden)
  }

  releasePreviewUrl() {
    if (!this.previewUrl) return

    URL.revokeObjectURL(this.previewUrl)
    this.previewUrl = null
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove('hidden')
    }
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = ''
      this.errorTarget.classList.add('hidden')
    }
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }
}
